import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import Slidebar from './Slidebar';
import { 
    User, 
    ShoppingBag, 
    Heart, 
    Bell, 
    MapPin, 
    Mail, 
    Phone,
    Settings,
    Package,
    Clock,
    TrendingUp,
    ArrowRight
} from 'lucide-react';

function Account() {
    const [profile, setProfile] = useState({});
    const [stats, setStats] = useState({
        orders: 0,
        wishlist: 0,
        notifications: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const userData = UserData();

    useEffect(() => {
        if (userData?.user_id) {
            // Fetch profile
            apiInstance.get(`user/profile/${userData.user_id}/`).then((res) => {
                setProfile(res.data);
            });

            // Fetch orders count
            apiInstance.get(`customer/orders/${userData.user_id}/`).then((res) => {
                const orders = Array.isArray(res.data) ? res.data : [];
                setStats(prev => ({ ...prev, orders: orders.length }));
                setRecentOrders(orders.slice(0, 3));
            });

            // Fetch wishlist count
            apiInstance.get(`customer/wishlist/${userData.user_id}/`).then((res) => {
                setStats(prev => ({ ...prev, wishlist: res.data.length }));
            });

            // Fetch notifications count
            apiInstance.get(`customer/notification/${userData.user_id}/`).then((res) => {
                setStats(prev => ({ ...prev, notifications: res.data.length }));
            });

            setLoading(false);
        }
    }, [userData?.user_id]);

    const quickActions = [
        { icon: ShoppingBag, label: 'Ver Pedidos', path: '/customer/orders/', color: '#10b981', bgColor: '#ecfdf5' },
        { icon: Heart, label: 'Lista de Deseos', path: '/customer/wishlist/', color: '#ef4444', bgColor: '#fef2f2' },
        { icon: Bell, label: 'Notificaciones', path: '/customer/notifications/', color: '#f59e0b', bgColor: '#fffbeb' },
        { icon: Settings, label: 'Configuración', path: '/customer/settings/', color: '#8b5cf6', bgColor: '#f5f3ff' },
    ];

    return (
        <div className="account-page">
            <div className="account-container">
                <div className="account-layout">
                    {/* Sidebar */}
                    <aside className="account-sidebar">
                        <Slidebar />
                    </aside>

                    {/* Main Content */}
                    <main className="account-main">
                        {/* Stats Cards */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                                    <Package size={24} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.orders}</span>
                                    <span className="stat-label">Total Pedidos</span>
                                </div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                                    <Heart size={24} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.wishlist}</span>
                                    <span className="stat-label">Favoritos</span>
                                </div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                                    <Bell size={24} />
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{stats.notifications}</span>
                                    <span className="stat-label">Notificaciones</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="section-card">
                            <h2 className="section-title">
                                <TrendingUp size={22} />
                                Acciones Rápidas
                            </h2>
                            <div className="quick-actions-grid">
                                {quickActions.map((action) => {
                                    const Icon = action.icon;
                                    return (
                                        <Link key={action.path} to={action.path} className="quick-action-card">
                                            <div className="action-icon" style={{ backgroundColor: action.bgColor, color: action.color }}>
                                                <Icon size={24} />
                                            </div>
                                            <span className="action-label">{action.label}</span>
                                            <ArrowRight size={18} className="action-arrow" />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Profile Info */}
                        <div className="section-card">
                            <div className="section-header">
                                <h2 className="section-title">
                                    <User size={22} />
                                    Información Personal
                                </h2>
                                <Link to="/customer/settings/" className="edit-link">
                                    Editar
                                    <ArrowRight size={16} />
                                </Link>
                            </div>
                            <div className="profile-info-grid">
                                <div className="info-item">
                                    <div className="info-icon">
                                        <Mail size={18} />
                                    </div>
                                    <div className="info-content">
                                        <span className="info-label">Email</span>
                                        <span className="info-value">{profile.user?.email || 'No especificado'}</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <div className="info-icon">
                                        <Phone size={18} />
                                    </div>
                                    <div className="info-content">
                                        <span className="info-label">Teléfono</span>
                                        <span className="info-value">{profile.user?.phone || 'No especificado'}</span>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <div className="info-icon">
                                        <MapPin size={18} />
                                    </div>
                                    <div className="info-content">
                                        <span className="info-label">Dirección</span>
                                        <span className="info-value">
                                            {profile.address ? `${profile.address}, ${profile.city || ''}` : 'No especificada'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Orders */}
                        {recentOrders.length > 0 && (
                            <div className="section-card">
                                <div className="section-header">
                                    <h2 className="section-title">
                                        <Clock size={22} />
                                        Pedidos Recientes
                                    </h2>
                                    <Link to="/customer/orders/" className="edit-link">
                                        Ver Todos
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>
                                <div className="orders-list">
                                    {recentOrders.map((order) => (
                                        <Link key={order.oid} to={`/customer/orders/${order.oid}/`} className="order-item">
                                            <div className="order-icon">
                                                <Package size={20} />
                                            </div>
                                            <div className="order-info">
                                                <span className="order-id">Pedido #{order.oid}</span>
                                                <span className="order-status" data-status={order.order_status}>
                                                    {order.order_status === 'fulfilled' ? 'Completado' : 
                                                     order.order_status === 'pending' ? 'Pendiente' : order.order_status}
                                                </span>
                                            </div>
                                            <span className="order-total">${order.total}</span>
                                            <ArrowRight size={18} className="order-arrow" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>

            <style>{`
                .account-page {
                    background: #ffffff;
                    min-height: 100vh;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                /* Layout */
                .account-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 32px 24px 60px;
                }

                .account-layout {
                    display: flex;
                    gap: 32px;
                }

                .account-sidebar {
                    width: 320px;
                    flex-shrink: 0;
                }

                .account-main {
                    flex: 1;
                    min-width: 0;
                }

                /* Stats */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: white;
                    border-radius: 20px;
                    padding: 24px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    flex-shrink: 0;
                }

                .stat-info {
                    display: flex;
                    flex-direction: column;
                }

                .stat-value {
                    font-size: 28px;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1;
                }

                .stat-label {
                    font-size: 13px;
                    color: #64748b;
                    margin-top: 4px;
                }

                /* Section Card */
                .section-card {
                    background: white;
                    border-radius: 24px;
                    padding: 28px;
                    margin-bottom: 24px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
                }

                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 20px;
                }

                .section-header .section-title {
                    margin: 0;
                }

                .edit-link {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #3b82f6;
                    text-decoration: none;
                    transition: gap 0.2s ease;
                }

                .edit-link:hover {
                    gap: 10px;
                }

                /* Quick Actions */
                .quick-actions-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 16px;
                }

                .quick-action-card {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    padding: 24px 16px;
                    background: #f8fafc;
                    border-radius: 16px;
                    text-decoration: none;
                    transition: all 0.3s ease;
                    position: relative;
                }

                .quick-action-card:hover {
                    background: #f1f5f9;
                    transform: translateY(-4px);
                }

                .action-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .action-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                }

                .action-arrow {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    color: #cbd5e1;
                    opacity: 0;
                    transition: all 0.2s ease;
                }

                .quick-action-card:hover .action-arrow {
                    opacity: 1;
                    color: #64748b;
                }

                /* Profile Info */
                .profile-info-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                }

                .info-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 16px;
                    background: #f8fafc;
                    border-radius: 14px;
                }

                .info-icon {
                    width: 40px;
                    height: 40px;
                    background: white;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #3b82f6;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                }

                .info-content {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    min-width: 0;
                }

                .info-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 500;
                }

                .info-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                    word-break: break-word;
                }

                /* Orders List */
                .orders-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .order-item {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: #f8fafc;
                    border-radius: 14px;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }

                .order-item:hover {
                    background: #f1f5f9;
                }

                .order-icon {
                    width: 44px;
                    height: 44px;
                    background: white;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #3b82f6;
                    flex-shrink: 0;
                }

                .order-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .order-id {
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                }

                .order-status {
                    font-size: 12px;
                    font-weight: 500;
                    color: #f59e0b;
                }

                .order-status[data-status="fulfilled"] {
                    color: #10b981;
                }

                .order-total {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                }

                .order-arrow {
                    color: #cbd5e1;
                }

                .order-item:hover .order-arrow {
                    color: #64748b;
                }

                /* Responsive */
                @media (max-width: 1024px) {
                    .account-sidebar {
                        display: none;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }

                    .quick-actions-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .profile-info-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 768px) {
                    .account-container {
                        padding: 20px 16px 40px;
                    }

                    .stats-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .stat-card {
                        padding: 20px;
                    }

                    .section-card {
                        padding: 20px;
                        border-radius: 20px;
                    }

                    .quick-actions-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }

                    .quick-action-card {
                        padding: 20px 12px;
                    }

                    .action-icon {
                        width: 48px;
                        height: 48px;
                    }
                }
            `}</style>
        </div>
    );
}

export default Account;